import { useState, useEffect, useCallback, useMemo } from "react";
import { X } from "lucide-react";


const LiveBookingFeed = ({latestOrders}) => {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Use dynamic bookings from API if available, otherwise fallback to default
  const feedBookings = useMemo(() => {
    if (latestOrders && Array.isArray(latestOrders)) {
       return latestOrders.map(order => {
          const createdAt = new Date(order.createdAt);
          const diffMs = Math.abs(new Date() - createdAt);
          const totalMins = Math.floor(diffMs / (60 * 1000));
          
          let timeAgo = "";
          if (totalMins < 60) {
            timeAgo = `${totalMins > 0 ? totalMins : 1} min ago`;
          } else {
            const hours = Math.floor(totalMins / 60);
            timeAgo = `${hours} hour${hours > 1 ? "s" : ""} ago`;
          }
          
          return {
             name: order.userName || order.customerName || "A devotee",
             language: order.languageName || "Hindi",
             ritual: order.poojaName || "Pooja",
             city: order.locationName || "Mumbai",
             timeAgo
          };
       });
    }
    return [];
  }, [latestOrders]);

  const showNext = useCallback(() => {
    if (dismissed || feedBookings.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % feedBookings.length);
    setVisible(true);
    setTimeout(() => setVisible(false), 5000);
  }, [dismissed, feedBookings]);

  useEffect(() => {
    if (feedBookings.length === 0) return;
    const initialTimer = setTimeout(showNext, 8000);
    return () => clearTimeout(initialTimer);
  }, [showNext, feedBookings]);

  useEffect(() => {
    if (currentIndex < 0 || dismissed || feedBookings.length === 0) return;
    const interval = setInterval(showNext, 22000);

    const handleVisibility = () => {
      if (document.hidden) setVisible(false);
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [currentIndex, showNext, dismissed, feedBookings]);

  if (currentIndex < 0 || dismissed || feedBookings.length === 0) return null;

  const booking = feedBookings[currentIndex];

  return (
    <div
      className={`fixed left-4 z-40 bottom-[72px] md:bottom-6 max-w-xs w-[calc(100vw-2rem)] transition-all duration-500 ease-out ${
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 pointer-events-none"
      }`}
    >
      <div className="bg-card border border-border border-l-4 border-l-primary rounded-lg shadow-lg p-3 pr-8 relative">
        <button
          onClick={() => { setVisible(false); setDismissed(true); }}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="flex items-center gap-1.5 mb-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-green-600">Live</span>
        </div>

        <p className="text-sm text-foreground leading-snug">
          <span className="font-semibold">{booking.name}</span> booked a{" "}
          <span className="font-medium text-primary">{booking.language}</span> priest for{" "}
          <span className="font-medium">{booking.ritual}</span> in {booking.city}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{booking.timeAgo}</p>
      </div>
    </div>
  );
};

export default LiveBookingFeed;
